import React from 'react';
import { Sparkles, Layers, Users } from 'lucide-react';
import CohortSelector from '../CohortSelector';

export const StepConfigureModel = ({
  selectedTemplate,
  selectedInterventions,
  interventions,
  modalities,
  scenarioConfig,
  setScenarioConfig,
  setConfiguringIntervention,
  setSelectedTemplate,
  setCurrentStep,
  setShowTemplates,
  selectedCohort,
  setSelectedCohort
}) => {
  return (
    <div>
      {/* Template Banner */}
      {selectedTemplate && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Sparkles className="text-blue-600" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">
                Using Template: {selectedTemplate.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {selectedTemplate.description}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedTemplate.expected_outcomes?.map((outcome, idx) => (
                  <span key={`outcome-${outcome.slice(0, 20)}-${idx}`} className="text-xs bg-white px-2 py-1 rounded-full text-gray-600 border">
                    {outcome}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedTemplate(null);
                setCurrentStep(0);
                setShowTemplates(true);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change template
            </button>
          </div>
        </div>
      )}
      
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Configure Your Solution</h2>
      <p className="text-gray-600 mb-6">
        Set delivery modality, audience size, and timeline for each intervention.
      </p>
      
      {/* Target Cohort Selection */}
      <div className="mb-6 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-teal-100 rounded-lg">
            <Users className="text-teal-600" size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Target Cohort</h3>
            <p className="text-sm text-gray-600 mb-3">
              Select a cohort to track this analysis against actual performance data. This enables cohort-specific Gap Analysis.
            </p>
            <CohortSelector
              selectedCohort={selectedCohort}
              onCohortChange={setSelectedCohort}
              placeholder="Select target cohort (optional)"
              showCreateOption={true}
              className="bg-white"
            />
            {selectedCohort && (
              <p className="text-xs text-teal-600 mt-2 flex items-center gap-1">
                <Users size={12} />
                Projections will be tracked for: {selectedCohort.name}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Selected Interventions Configuration */}
      <div className="space-y-4 mb-6">
        {Object.entries(selectedInterventions).map(([id, config]) => {
          const intervention = interventions.find(i => i.id === id);
          const modality = modalities.find(m => m.id === config.modality_id);
          if (!intervention) return null;
          
          return (
            <div key={id} className="border-2 border-blue-600 rounded-xl overflow-hidden bg-blue-50">
              {/* Intervention Title Header */}
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <h4 className="text-lg font-semibold text-blue-900">{intervention.name}</h4>
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                    {intervention.category}
                  </span>
                </div>
                <button
                  onClick={() => setConfiguringIntervention(id)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm shadow-sm"
                  data-testid={`configure-step3-btn-${id}`}
                >
                  <Layers size={16} />
                  Configure
                </button>
              </div>
              {/* Intervention Details */}
              <div className="grid grid-cols-3 gap-4 px-5 py-4 border-t border-blue-200 bg-white/50">
                <div>
                  <span className="text-sm text-gray-500 block">Modality:</span>
                  <p className="font-medium text-gray-900">{modality?.name || 'Not set'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">Audience:</span>
                  <p className="font-medium text-gray-900">{config.target_audience_size} people</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">Est. Cost:</span>
                  <p className="font-semibold text-gray-900">
                    ${((config.cost_override || intervention.typical_cost_per_person) * (modality?.cost_multiplier || 1) * config.target_audience_size).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Scenario Settings */}
      <div className="border-t pt-6">
        <h4 className="font-medium text-gray-900 mb-4">Timeline Settings</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm text-gray-500">Time Horizon</label>
            <select
              value={scenarioConfig.time_horizon_months}
              onChange={(e) => setScenarioConfig(prev => ({ ...prev, time_horizon_months: parseInt(e.target.value) }))}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
              data-testid="timeline-time-horizon"
            >
              <option value={6}>6 months</option>
              <option value={12}>12 months</option>
              <option value={24}>24 months</option>
              <option value={36}>36 months</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-500">Implementation Lag</label>
            <select
              value={scenarioConfig.implementation_lag_months}
              onChange={(e) => setScenarioConfig(prev => ({ ...prev, implementation_lag_months: parseInt(e.target.value) }))}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
              data-testid="timeline-implementation-lag"
            >
              <option value={1}>1 month</option>
              <option value={2}>2 months</option>
              <option value={3}>3 months</option>
              <option value={6}>6 months</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-500">Ramp-up Period</label>
            <select
              value={scenarioConfig.ramp_up_months}
              onChange={(e) => setScenarioConfig(prev => ({ ...prev, ramp_up_months: parseInt(e.target.value) }))}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
              data-testid="timeline-ramp-up"
            >
              <option value={1}>1 month</option>
              <option value={2}>2 months</option>
              <option value={3}>3 months</option>
              <option value={6}>6 months</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-500">Include Decay</label>
            <select
              value={scenarioConfig.include_decay ? 'yes' : 'no'}
              onChange={(e) => setScenarioConfig(prev => ({ ...prev, include_decay: e.target.value === 'yes' }))}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
              data-testid="timeline-include-decay"
            >
              <option value="no">No</option>
              <option value="yes">Yes (15%/year)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};
